import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { FaArrowLeft, FaFileExcel, FaFileAlt } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const AccountantSubePaymentDetailsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId, userType } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [allWorks, setAllWorks] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!userId) {
      navigate('/accountant/subhe-payment-list');
      return;
    }

    setLoading(true);
    const userRef = ref(db, `ekamwatapzp/${userType === 'Society' ? 'SocietyRegistrations' : 'EngineerRegistrations'}/${userId}`);
    const worksRef = ref(db, 'ekamwatapzp/WorkNames');

    // Fetch Work Names for lookup
    onValue(worksRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const worksMap = {};
        Object.values(data).forEach(meetingWorks => {
          Object.values(meetingWorks).forEach(work => {
            worksMap[work.workId || work.workNumber] = work;
          });
        });
        setAllWorks(worksMap);
      }
    });

    // Fetch User and Payment Data
    onValue(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const user = snapshot.val();
        setUserData(user);

        const workPayments = user.work_payment || {};
        const generalPayments = user.payments || {};

        const flattenedWork = Object.entries(workPayments).map(([id, p]) => ({
          id,
          ...p,
          type: 'Work Payment',
          dateDisplay: p.date ? new Date(p.date).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }) : 'N/A',
          rawDate: p.date ? new Date(p.date) : new Date(0),
          transactionId: p.userUpiId || p.transactionId || p.receiptId || 'N/A'
        }));

        const flattenedGeneral = Object.entries(generalPayments).map(([type, p]) => ({
          id: type,
          ...p,
          type: type,
          dateDisplay: p.date ? new Date(p.date).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }) : 'N/A',
          rawDate: p.date ? new Date(p.date) : new Date(0),
          transactionId: p.userUpiId || p.transactionId || p.receiptId || 'N/A'
        }));

        const combined = [...flattenedWork, ...flattenedGeneral];

        // Sort by date descending
        combined.sort((a, b) => b.rawDate - a.rawDate);
        setPayments(combined);
      }
      setLoading(false);
    });
  }, [userId, userType, navigate]);

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(payments.map((p, i) => ({
      '#': i + 1,
      'Name': userData?.firstName ? `${userData.firstName} ${userData.surname}` : (userData?.societyName || 'N/A'),
      'Mobile': userData?.mobile || userData?.whatsappNo || userData?.chairmanWhatsapp || 'N/A',
      'Payment Type': p.type || 'N/A',
      'Challan No.': p.receiptId || 'N/A',
      'Transaction Id': p.transactionId || 'N/A',
      'Amount': p.paidAmount || '0.00',
      'Work IDs': (p.selectedWorks || []).map(w => w.split(' - ')[0]).join(','),
      'Status': p.status || 'N/A',
      'Date': p.dateDisplay
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PaymentDetails");
    XLSX.writeFile(wb, `${userData?.firstName || userData?.societyName}_Payments.xlsx`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const name = userData?.firstName ? `${userData.firstName} ${userData.middleName || ''} ${userData.surname || ''}`.trim() : (userData?.societyName || 'N/A');
  const mobile = userData?.mobile || userData?.whatsappNo || userData?.chairmanWhatsapp || 'N/A';

  return (
    <div className="min-h-screen bg-[#f4f7fe] p-4 md:p-8 font-sans">
      <div className="max-w-[98%] mx-auto bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        
        {/* Header Section */}
        <div className="bg-white px-8 py-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Payment Details</h1>
            <p className="text-gray-400 text-sm font-medium mt-1">
              सांगली जिल्हा परिषद / <span className="font-bold">Payment Details</span>
            </p>
          </div>
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold text-sm transition-all shadow-md active:scale-95"
          >
            <FaArrowLeft size={14} /> Back
          </button>
        </div>

        <div className="p-8">
          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
            <div className="flex gap-2 w-full md:w-auto">
              <button 
                onClick={handleExport}
                className="flex items-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold text-sm transition-all shadow-sm active:scale-95"
              >
                <FaFileExcel /> Download Excel
              </button>
            </div>
            
            <div className="flex items-center gap-2 w-full md:w-auto">
              <span className="text-sm font-bold text-gray-500 whitespace-nowrap">Search:</span>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64 px-4 py-2 border border-gray-200 rounded text-sm font-medium focus:ring-2 focus:ring-indigo-500/20 outline-none transition bg-gray-50"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#f8f9fa] border-b border-gray-200">
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center w-16">#</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">Name</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">Mobile No.</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">Payment Type</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">Challan No.</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">Transaction Id</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">Amount</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">Status</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">Date</th>
                  <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest text-center">View Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 italic">
                {payments.length > 0 ? (
                  payments.map((p, index) => (
                    <tr key={index} className="hover:bg-indigo-50/20 transition-all font-medium text-gray-700">
                      <td className="px-6 py-4 border-r text-center text-sm font-bold">{index + 1}</td>
                      <td className="px-6 py-4 border-r text-sm font-bold text-gray-800">{name}</td>
                      <td className="px-6 py-4 border-r text-sm font-bold text-gray-600">{mobile}</td>
                      <td className="px-6 py-4 border-r text-center text-xs font-bold">
                        <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full">{p.type}</span>
                      </td>
                      <td className="px-6 py-4 border-r text-sm font-bold text-blue-600">{p.receiptId || 'N/A'}</td>
                      <td className="px-6 py-4 border-r text-sm font-bold text-gray-500">{p.transactionId || 'N/A'}</td>
                      <td className="px-6 py-4 border-r text-sm font-bold text-gray-800">{parseFloat(p.paidAmount || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 border-r text-center text-xs font-black">
                        <span className={`px-3 py-1 rounded-full border ${
                          p.status === 'success' || p.status === 'Completed' ? 'bg-green-50 text-green-600 border-green-100' : 
                          p.status === 'pending' || p.status === 'Pending' ? 'bg-amber-50 text-amber-600 border-amber-100' : 
                          'bg-red-50 text-red-600 border-red-100'
                        }`}>
                          {p.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 border-r text-sm font-bold text-gray-500">{p.dateDisplay}</td>
                      <td className="px-6 py-4 text-center">
                        <button className="text-gray-400 hover:text-indigo-600 transition-colors">
                          <FaFileAlt size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-gray-400 font-bold bg-[#f8f9fa]">
                      No payment historical records found for this user.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AccountantSubePaymentDetailsList;
